import React, { useState, useRef } from "react";
import { Box, Stepper, Step, StepButton } from "@mui/material";
import useSocket from "./hooks/webSocket";
import ConfigurationStep from "./components/fip-process/ConfigurationStep";
import TemplateStep from "./components/fip-process/TemplateStep";
import UploadStep from "./components/fip-process/UploadStep";
import ValidationStep from "./components/fip-process/ValidationStep";
import ProcessingStep from "./components/fip-process/ProcessingStep";

const steps = ["Configuration", "Template", "Upload", "Validation", "Process"];

const ADIM = () => {
  const [activeStep,           setActiveStep]           = useState(0);
  const [validationResult,     setValidationResult]     = useState(null);
  const [file,                 setFile]                 = useState(null);
  const [matterProcessLoading, setMatterProcessLoading] = useState(false);
  const [matterProcessingResult, setMatterProcessingResult] = useState({});
  const [configValues,         setConfigValues]         = useState({ type: "patent", action: "create" });
  const [processError,         setProcessError]         = useState(null);
  const [interrupted,          setInterrupted]          = useState(false);
  const [reopenCredentials,    setReopenCredentials]    = useState(false);

  const activeJobIdRef = useRef(null);

  const { rows, progress, totalRows, joinJob, resetRows, socketConnected } = useSocket(
    activeJobIdRef,
    (data) => {
      setMatterProcessLoading(false);
      setMatterProcessingResult(data ?? {});
    },
    (message) => {
      setMatterProcessLoading(false);
      setProcessError(message);
    },
    () => {
      setMatterProcessLoading(false);
      setInterrupted(true);
    }
  );

  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => setActiveStep((prev) => prev - 1);

  const handleStepClick = (index) => {
    if (index >= activeStep || matterProcessLoading) return;
    if (index <= 2) {
      setFile(null);
      setValidationResult(null);
    }
    setMatterProcessingResult({});
    resetRows();
    setActiveStep(index);
  };

  const handleProcessReset = () => {
    activeJobIdRef.current = null;
    setProcessError(null);
    setInterrupted(false);
    setMatterProcessingResult({});
    resetRows();
  };

  const activateJob = (id) => {
    activeJobIdRef.current = id;
  };

  const handleStartNewUpload = () => {
    setFile(null);
    setValidationResult(null);
    setMatterProcessingResult({});
    setProcessError(null);
    setInterrupted(false);
    setReopenCredentials(false);
    activeJobIdRef.current = null;
    resetRows();
    setActiveStep(0);
  };

  const handleRetryCredentials = () => {
    setProcessError(null);
    setInterrupted(false);
    setMatterProcessingResult({});
    activeJobIdRef.current = null;
    resetRows();
    setReopenCredentials(true);
    setActiveStep(3);
  };

  const isAuthError = /auth|credential/i.test(processError || "");

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <ConfigurationStep
            onNext={handleNext}
            configValues={configValues}
            setConfigValues={setConfigValues}
          />
        );
      case 1:
        return <TemplateStep onNext={handleNext} onBack={handleBack} configValues={configValues} />;
      case 2:
        return (
          <UploadStep
            onNext={handleNext}
            onBack={handleBack}
            setValidationResult={setValidationResult}
            file={file}
            setFile={setFile}
            configValues={configValues}
          />
        );
      case 3:
        return (
          <ValidationStep
            onNext={handleNext}
            validationResult={validationResult}
            onBack={handleBack}
            file={file}
            setFile={setFile}
            setMatterProcessLoading={setMatterProcessLoading}
            setResult={setMatterProcessingResult}
            activateJob={activateJob}
            joinJob={joinJob}
            configValues={configValues}
            onProcessReset={handleProcessReset}
            setProcessError={setProcessError}
            autoOpenCredentials={reopenCredentials}
            onCredentialsOpened={() => setReopenCredentials(false)}
          />
        );
      case 4:
        return (
          <ProcessingStep
            loading={matterProcessLoading}
            setLoading={setMatterProcessLoading}
            result={matterProcessingResult}
            rows={rows}
            totalRows={totalRows}
            progress={progress}
            onStartNewUpload={handleStartNewUpload}
            onRetryCredentials={isAuthError ? handleRetryCredentials : undefined}
            configValues={configValues}
            processError={processError}
            socketConnected={socketConnected}
            interrupted={interrupted}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ p: 4 }}>
      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label, index) => (
          <Step key={label}>
            <StepButton
              onClick={() => handleStepClick(index)}
              disabled={index >= activeStep || matterProcessLoading}
            >
              {label}
            </StepButton>
          </Step>
        ))}
      </Stepper>

      <Box sx={{ minHeight: 250 }}>
        {renderStepContent()}
      </Box>
    </Box>
  );
};

export default ADIM;
